import {useCallback, useEffect, useRef, useState} from 'react';

interface NavigableAction {
  id: string;
  onTrigger: () => void;
  closeOnTrigger?: boolean;
}

interface UseOmniboxKeyboardNavOptions {
  actions: Array<NavigableAction>;
  enabled: boolean;
  onClose: () => void;
}

interface UseOmniboxKeyboardNavResult {
  hoveredIndex: number;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  handleMouseEnter: (index: number) => void;
  handleMouseMove: () => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

export default function useOmniboxKeyboardNav({
  actions,
  enabled,
  onClose,
}: UseOmniboxKeyboardNavOptions): UseOmniboxKeyboardNavResult {
  const [hoveredIndex, setHoveredIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isKeyboardNavigating = useRef(false);

  // Clamp hovered index when actions change (avoids resetting position on display-only updates)
  useEffect(() => {
    setHoveredIndex((prev) => Math.min(prev, Math.max(0, actions.length - 1)));
  }, [actions]);

  const scrollToIndex = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const el = container.querySelector(`[data-action-index="${index}"]`);
    if (el) {
      el.scrollIntoView({block: 'nearest'});
    }
  }, []);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled || actions.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        isKeyboardNavigating.current = true;
        const next = (hoveredIndex + 1) % actions.length;
        setHoveredIndex(next);
        scrollToIndex(next);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        isKeyboardNavigating.current = true;
        const next = (hoveredIndex - 1 + actions.length) % actions.length;
        setHoveredIndex(next);
        scrollToIndex(next);
      } else if (event.key === 'Enter' && actions[hoveredIndex]) {
        event.preventDefault();
        event.stopPropagation();
        const action = actions[hoveredIndex];
        action.onTrigger();
        if (action.closeOnTrigger !== false) {
          onClose();
        }
      }
    },
    [enabled, actions, hoveredIndex, scrollToIndex, onClose]
  );

  // scrollIntoView triggers mouseenter without mousemove, so we use
  // mousemove to distinguish real mouse activity from scroll-induced hovers.
  const handleMouseMove = useCallback(() => {
    isKeyboardNavigating.current = false;
  }, []);

  const handleMouseEnter = useCallback((index: number) => {
    if (!isKeyboardNavigating.current) {
      setHoveredIndex(index);
    }
  }, []);

  return {hoveredIndex, handleKeyDown, handleMouseEnter, handleMouseMove, scrollContainerRef};
}
